import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useFormDocuments, type FormDocumentWithTemplateName } from "@/hooks/use-form-documents";
import { FileText, ChevronDown, ChevronRight, Plus, Loader2, Link as LinkIcon, X, Download, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { fetchPdfBlob } from "@/lib/pdf-blob-fetcher";
import { env } from "@/config/env";
import { auth } from "@/lib/firebase";
import { SendFormModal } from "./modals/SendFormModal";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface FormsModuleProps {
    claimId: number;
    isOpen: boolean;
    onToggle: () => void;
    canWrite: boolean;
}

export function FormsModule({ claimId, isOpen, onToggle, canWrite }: FormsModuleProps) {
    const { 
        formDocuments, 
        isLoading, 
        templates, 
        isLoadingTemplates,
        sendFormAsync, 
        isSending,
        cancelForm,
        isCancelling,
        deleteFormAsync
    } = useFormDocuments(claimId);
    const { toast } = useToast();

    const [isSendDialogOpen, setIsSendDialogOpen] = useState(false);
    const [formToCancel, setFormToCancel] = useState<number | null>(null);
    const [formToDelete, setFormToDelete] = useState<FormDocumentWithTemplateName | null>(null);

    // Cancelled forms are hidden from the list (the record is retained server-side
    // for the audit trail, and the homeowner's access link is revoked).
    const visibleForms = formDocuments.filter((form) => form.status !== 'cancelled');

    const handleSendForm = async (data: { formTemplateId: number; recipientName: string; recipientEmail: string }) => {
        if (!canWrite) return;
        try {
            await sendFormAsync(data);
            setIsSendDialogOpen(false);
        } catch {
            // Error toast is handled by the mutation's onError callback
        }
    };

    const getStatusBadge = (status: string) => {
        const statusConfig = {
            sent: { variant: 'secondary' as const, label: 'Sent', className: '' },
            opened: { variant: 'secondary' as const, label: 'Opened', className: 'bg-blue-100 text-blue-800' },
            completed: { variant: 'default' as const, label: 'Completed', className: 'bg-green-100 text-green-800' },
            cancelled: { variant: 'destructive' as const, label: 'Cancelled', className: '' },
        };
        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.sent;
        return <Badge variant={config.variant} className={`text-[10px] h-4 px-1.5 ${config.className}`}>{config.label}</Badge>;
    };

    const copyFormLink = async (accessToken: string) => {
        const url = `${window.location.origin}/forms/${accessToken}`;
        
        try {
            await navigator.clipboard.writeText(url);
            toast({
                title: 'Link copied',
                description: 'Form link copied to clipboard',
            });
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            toast({
                title: 'Failed to copy link',
                description: 'Could not copy to clipboard. Please copy manually.',
                variant: 'destructive',
            });
        }
    };

    const downloadCompletedPdf = async (claimId: number, form: FormDocumentWithTemplateName) => {
        try {
            const getToken = async () => {
                if (auth.currentUser) {
                    return await auth.currentUser.getIdToken();
                }
                throw new Error('Not authenticated');
            };

            const pdfBlob = await fetchPdfBlob(
                `/api/claims/${claimId}/forms/${form.id}/pdf`,
                env.apiBaseUrl,
                getToken
            );

            const safeName = (s: string) => s.replace(/[^a-zA-Z0-9 _-]/g, '').trim().replace(/\s+/g, '_');
            const filename = `${safeName(form.formTemplateName || 'Form')}-${safeName(form.recipientName || 'Unknown')}.pdf`;

            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast({
                title: 'Failed to download PDF',
                description: error instanceof Error ? error.message : 'An error occurred',
                variant: 'destructive',
            });
        }
    };

    const handleCancelForm = (formDocId: number) => {
        setFormToCancel(formDocId);
    };

    const confirmCancelForm = () => {
        if (formToCancel && canWrite) {
            cancelForm(formToCancel);
            setFormToCancel(null);
        }
    };

    return (
        <>
            <Card className="shadow-xs w-full max-w-full overflow-hidden flex-shrink-0">
                <Collapsible open={isOpen} onOpenChange={onToggle}>
                    <CollapsibleTrigger className="w-full">
                        <CardHeader className="py-3 px-4 hover:bg-muted/50 cursor-pointer transition-colors overflow-hidden w-full">
                            <div className="flex items-center justify-between min-w-0 w-full gap-2">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <FileText className="h-4 w-4 shrink-0" />
                                    <CardTitle className="text-sm font-medium truncate">Forms</CardTitle>
                                    <Badge variant="secondary" className="text-xs h-5 px-1.5 min-w-[1.25rem] shrink-0 flex items-center justify-center">
                                        {visibleForms.length}
                                    </Badge>
                                </div>
                                {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                            </div>
                        </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <CardContent className="px-4 pb-4 pt-0 w-full overflow-hidden">
                            <div className="pb-2 border-b mb-1"></div>

                            {isLoading ? (
                                <div className="flex justify-center p-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
                            ) : visibleForms.length === 0 ? (
                                <div className="text-center py-6 text-sm text-muted-foreground">
                                    No forms sent
                                </div>
                            ) : (
                                <div className="space-y-2 pt-2 max-h-[300px] overflow-y-auto pr-1 overflow-x-hidden scrollbar-thin scrollbar-thumb-muted-foreground/20 hover:scrollbar-thumb-muted-foreground/40 w-full max-w-full">
                                    {visibleForms.map((form: FormDocumentWithTemplateName) => (
                                        <div key={form.id} className="p-2 rounded-md hover:bg-muted/50 group min-w-0 w-full overflow-hidden border">
                                            <div className="flex items-start justify-between gap-2 mb-1">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-medium truncate w-full" title={form.formTemplateName || 'Unnamed Template'}>
                                                        {form.formTemplateName || 'Unnamed Template'}
                                                    </p>
                                                    <p className="text-[10px] text-muted-foreground truncate w-full">
                                                        To: {form.recipientName || 'Unknown'}
                                                    </p>
                                                </div>
                                                {getStatusBadge(form.status)}
                                            </div>
                                            <p className="text-[10px] text-muted-foreground mb-2">
                                                Sent {format(new Date(form.sentAt), "MMM d, yyyy")}
                                                {form.status === 'completed' && form.signedAt && (
                                                    <> • Completed {format(new Date(form.signedAt), "MMM d, yyyy")}</>
                                                )}
                                            </p>
                                            <div className="flex gap-1">
                                                {form.status === 'completed' ? (
                                                    <Button
                                                        variant="default"
                                                        size="sm"
                                                        className="h-6 text-[10px] flex-1"
                                                        onClick={() => downloadCompletedPdf(claimId, form)}
                                                    >
                                                        <Download className="h-3 w-3 mr-1" />
                                                        Download PDF
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-6 text-[10px] flex-1"
                                                        onClick={() => copyFormLink(form.accessToken)}
                                                    >
                                                        <LinkIcon className="h-3 w-3 mr-1" />
                                                        Copy Link
                                                    </Button>
                                                )}
                                                {(form.status === 'sent' || form.status === 'opened') && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                                        onClick={() => canWrite && handleCancelForm(form.id)}
                                                        disabled={isCancelling || !canWrite}
                                                        title="Cancel form"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </Button>
                                                )}
                                                {form.status === 'completed' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                                        onClick={() => canWrite && setFormToDelete(form)}
                                                        disabled={!canWrite}
                                                        title="Delete form"
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="pt-3 border-t mt-3 w-full">
                                <Button
                                    size="sm"
                                    className="w-full h-8 text-xs"
                                    onClick={() => canWrite && setIsSendDialogOpen(true)}
                                    disabled={!canWrite || isLoadingTemplates || templates.length === 0}
                                >
                                    <Plus className="h-3 w-3 mr-2" />
                                    Send Form
                                </Button>
                                {templates.length === 0 && !isLoadingTemplates && (
                                    <p className="text-[10px] text-muted-foreground text-center mt-1">
                                        No templates available
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </CollapsibleContent>
                </Collapsible>
            </Card>

            {/* Send Form Dialog */}
            <SendFormModal
                isOpen={isSendDialogOpen}
                onClose={() => setIsSendDialogOpen(false)}
                templates={templates}
                onSubmit={handleSendForm}
                isSending={isSending}
            />

            {/* Cancel Form Confirmation */}
            <AlertDialog open={formToCancel !== null} onOpenChange={(open) => !open && setFormToCancel(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Cancel Form?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to cancel this form? The homeowner will no longer be able to access it.
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Keep Form</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={confirmCancelForm}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Cancel Form
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Completed Form Confirmation */}
            <ConfirmDeleteDialog
                open={formToDelete !== null}
                onOpenChange={(open) => { if (!open) setFormToDelete(null); }}
                onConfirm={async () => { if (canWrite && formToDelete) await deleteFormAsync(formToDelete.id); }}
                title="Delete Form"
                description={
                    formToDelete
                        ? `Are you sure you want to delete "${formToDelete.formTemplateName || 'this form'}"? The completed form and its PDF will be permanently removed. This action cannot be undone.`
                        : ""
                }
            />
        </>
    );
}
